/*------------------------------------------------------------------------------------------
* © 2020 KPMG LLP, a Delaware limited liability partnership and 
* a member firm of the KPMG global organization of independent member firms 
* affiliated with KPMG International Limited, a private English company limited by guarantee. 
* All rights reserved.
*-----------------------------------------------------------------------------------------*/
import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { mergeMap, catchError, switchMap } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import { EnvSettingsService } from '../services/env-settings.service';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';


@Injectable({
  providedIn: 'root'
})
export class HttpConfigInterceptorService implements HttpInterceptor {


  constructor(private _commonService: CommonService, private _envSettings: EnvSettingsService, private msalService: MsalService) {
  }


  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const appId = this._commonService.getAppId();
    const reqUrl: string = request.urlWithParams; // Get Url with Parameters
    const paramIndex: number = reqUrl.indexOf('?'); // first index of ?
    const reqUrlWithoutParams: string = paramIndex === -1 ? reqUrl : reqUrl.substring(0, paramIndex); // Base Url

    // Existings Params from request which are part of url.
    const reqUrlParams: string = paramIndex === -1 ? '' : reqUrl.substring(paramIndex + 1, reqUrl.length);
    let httpParams: HttpParams = new HttpParams({
      fromString: request.params.toString() || reqUrlParams
    });

    // If request is from Project Level
    if (appId) {
      httpParams = httpParams.set('appId', appId);
    }

    // Determine if additional headers are needed
    return this.addHeaders(reqUrlWithoutParams, request.headers).pipe(
      mergeMap(headers => {
        // construct new HttpRequest with new url
        const newReq: HttpRequest<any> = request.clone({
          url: `${reqUrlWithoutParams}?${httpParams.toString()}`,
          headers: headers,
          withCredentials: true
        });

        return next.handle(newReq).pipe(
          catchError((error: any) => {
            const errorObject: any = {
              type: 'Http Error',
              method: newReq.method,
              url: newReq.urlWithParams,
              message: error.message,
              statusCode: error.status,
              statusText: error.statusText,
              body: newReq.body,
              raw: {
                request: newReq,
                error: error
              }
            };
            return throwError(error);
          })
        );
      })
    );


  }

  private addHeaders(url: string, existingHeaders: HttpHeaders): Observable<HttpHeaders> {
    let headers: HttpHeaders = existingHeaders.set('Content-Type', 'application/json');

    if (this._envSettings.azure.isCloud) {
      return this.msalService.initialize().pipe(
        switchMap(() => {
          let account = this.msalService.instance.getActiveAccount();
          if (!account) {
            // Active account not set (e.g. after page refresh) — fall back to first cached account.
            const accounts = this.msalService.instance.getAllAccounts();
            if (accounts.length === 0) {
              return throwError(new Error('No authenticated account found. Please log in.'));
            }
            account = accounts[0];
            this.msalService.instance.setActiveAccount(account);
          }

          return this.msalService.acquireTokenSilent({
            scopes: [`api://${this._envSettings.azure.clientId}/user_impersonation`],
            account: account
          }).pipe(
            mergeMap((response: AuthenticationResult) => {
              headers = headers.set('Authorization', `Bearer ${response.accessToken}`);
              return of(headers);
            }),
            catchError((error) => {
              if (error instanceof InteractionRequiredAuthError) {
                this.msalService.acquireTokenRedirect({
                  scopes: [`api://${this._envSettings.azure.clientId}/user_impersonation`]
                });
              }
              console.error(error);
              return throwError(error);
            })
          );
        })
      );
    } else {
      console.log('Adding headers in Non-Cloud Mode');
      return of(headers);
    }
  }
}

